import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../api";
import { PageTitle, TH, TD, Badge } from "./AdminLayout";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function CommandsAdmin() {
  const [commands, setCommands] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", usage: "", category: "Custom" });

  const load = () => api.get("/admin/commands").then((r) => setCommands(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/commands/${id}`, body);
      toast.success("Command updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/commands", form);
      toast.success(`!${form.name} created`);
      setCreating(false);
      setForm({ name: "", description: "", usage: "", category: "Custom" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete !${c.name}?`)) return;
    await api.delete(`/admin/commands/${c.id}`);
    toast.success(`!${c.name} deleted`);
    load();
  };

  return (
    <div data-testid="admin-commands">
      <PageTitle title="Command Management" sub={`${commands.length} commands · enable, edit or create new ones.`}>
        <button onClick={() => setCreating(true)} data-testid="command-create-open"
          className="inline-flex items-center gap-2 bg-blurple hover:bg-[#4752c4] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-150">
          <Plus size={15} /> New command
        </button>
      </PageTitle>

      <div className="rounded-lg border border-white/5 bg-panel overflow-x-auto">
        <table className="w-full min-w-[860px]" data-testid="commands-table">
          <thead className="border-b border-white/5"><tr><TH>Command</TH><TH>Category</TH><TH>Description</TH><TH>Usage</TH><TH>Uses</TH><TH>Enabled</TH><TH /></tr></thead>
          <tbody className="divide-y divide-white/5">
            {commands.map((c) => (
              <tr key={c.id} className={!c.enabled ? "opacity-45" : ""} data-testid={`command-row-${c.name.replace(/\s+/g, "-")}`}>
                <TD><code className="font-mono text-volt">!{c.name}</code>{c.premium && <span className="ml-2"><Badge tone="amber">premium</Badge></span>}</TD>
                <TD><Badge tone="blue">{c.category}</Badge></TD>
                <TD className="max-w-[320px]"><span className="line-clamp-2 text-white/60 text-xs">{c.description}</span></TD>
                <TD><code className="font-mono text-xs text-white/50">{c.usage}</code></TD>
                <TD className="font-mono">{(c.usage_count || 0).toLocaleString()}</TD>
                <TD><Switch checked={c.enabled} onCheckedChange={(v) => patch(c.id, { enabled: v })} data-testid={`command-toggle-${c.name.replace(/\s+/g, "-")}`} /></TD>
                <TD>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditing(c); setForm({ name: c.name, description: c.description, usage: c.usage, category: c.category }); }} className="p-2 text-white/40 hover:text-white transition-colors duration-150" data-testid={`command-edit-${c.name.replace(/\s+/g, "-")}`} aria-label="Edit"><Pencil size={14} /></button>
                    <button onClick={() => remove(c)} className="p-2 text-white/40 hover:text-red-400 transition-colors duration-150" data-testid={`command-delete-${c.name.replace(/\s+/g, "-")}`} aria-label="Delete"><Trash2 size={14} /></button>
                  </div>
                </TD>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={creating || !!editing} onOpenChange={() => { setCreating(false); setEditing(null); }}>
        <DialogContent className="bg-panel border-white/10 text-white" data-testid="command-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? `Edit !${editing.name}` : "Create command"}</DialogTitle></DialogHeader>
          <form onSubmit={editing ? (e) => { e.preventDefault(); patch(editing.id, { description: form.description, usage: form.usage, category: form.category }); } : create} className="space-y-4 mt-2">
            {!editing && (
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-white/50">Name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase().replace(/[^a-z0-9 ]/g, "") })} required data-testid="command-name-input"
                  className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blurple" placeholder="vibe" />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-white/50">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required rows={3} data-testid="command-description-input"
                className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blurple resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-white/50">Usage</label>
                <input value={form.usage} onChange={(e) => setForm({ ...form, usage: e.target.value })} data-testid="command-usage-input"
                  className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blurple" placeholder="!vibe <mood>" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-white/50">Category</label>
                <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="command-category-input"
                  className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blurple" />
              </div>
            </div>
            <button type="submit" data-testid="command-save"
              className="w-full bg-blurple hover:bg-[#4752c4] text-white font-semibold py-3 rounded-full transition-colors duration-150">
              {editing ? "Save changes" : "Create command"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
